import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, Check, ExternalLink, Flame, Zap, Droplet, Home as HomeIcon, Smartphone, Car, Tv, Wifi, CreditCard, Landmark, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";

// Bills Monitor — user-personal utility bill tracker with due-date warnings +
// a one-tap "Pay" that opens the biller's own pay_url. We never charge here — this
// is a reminder collection, not a payment gateway.

const KINDS = [
  { k: "gas",         label: "Gas",         Icon: Flame,      color: "text-orange-600 bg-orange-50" },
  { k: "electric",    label: "Electric",    Icon: Zap,        color: "text-amber-600 bg-amber-50" },
  { k: "water",       label: "Water",       Icon: Droplet,    color: "text-sky-600 bg-sky-50" },
  { k: "house_tax",   label: "House tax",   Icon: HomeIcon,   color: "text-stone-700 bg-stone-100" },
  { k: "mobile",      label: "Mobile",      Icon: Smartphone, color: "text-indigo-600 bg-indigo-50" },
  { k: "toll",        label: "Toll",        Icon: Car,        color: "text-fuchsia-600 bg-fuchsia-50" },
  { k: "cable",       label: "Cable",       Icon: Tv,         color: "text-rose-600 bg-rose-50" },
  { k: "internet",    label: "Internet",    Icon: Wifi,       color: "text-purple-600 bg-purple-50" },
  { k: "credit_card", label: "Credit card", Icon: CreditCard, color: "text-red-600 bg-red-50" },
  { k: "loan",        label: "Loan EMI",    Icon: Landmark,   color: "text-emerald-700 bg-emerald-50" },
  { k: "insurance",   label: "Insurance",   Icon: Shield,     color: "text-teal-700 bg-teal-50" },
  { k: "other",       label: "Other",       Icon: CreditCard, color: "text-stone-700 bg-stone-100" },
];
const KIND_META = Object.fromEntries(KINDS.map(k => [k.k, k]));

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).setHours(0, 0, 0, 0);
  const now = new Date().setHours(0, 0, 0, 0);
  return Math.round((t - now) / 86400_000);
};

export default function Bills() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("upcoming");   // upcoming | overdue | paid | all
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useSeo({ title: "My Bills Monitor", description: "Track gas, electric, water, mobile and all utility bills with one-tap Pay.", canonical: "/bills" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bills");
      setItems(r.data.items || []);
    } catch { toast.error("Could not load bills"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user?.user_id]);

  const summary = useMemo(() => {
    const unpaid = items.filter(i => !i.is_paid);
    const overdue = unpaid.filter(i => (daysUntil(i.due_date) ?? 0) < 0);
    const dueSoon = unpaid.filter(i => { const d = daysUntil(i.due_date); return d !== null && d >= 0 && d <= 7; });
    const total = unpaid.reduce((s, i) => s + Number(i.amount_inr || 0), 0);
    return { unpaid, overdue, dueSoon, total };
  }, [items]);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "paid") return items.filter(i => i.is_paid);
    if (tab === "overdue") return summary.overdue;
    return summary.unpaid.filter(i => (daysUntil(i.due_date) ?? 0) >= 0);
  }, [tab, items, summary]);

  const markPaid = async (b) => {
    try {
      const r = await api.patch(`/bills/${b.bill_id}`, { is_paid: true });
      setItems(x => x.map(i => i.bill_id === b.bill_id ? { ...i, is_paid: true } : i));
      if (r.data.spawned) {
        setItems(x => [r.data.spawned, ...x]);
        toast.success("Paid · next month rolled forward");
      } else {
        toast.success("Marked paid");
      }
    } catch { toast.error("Could not update"); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    try { await api.delete(`/bills/${id}`); setItems(x => x.filter(i => i.bill_id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  if (!user) return <div className="max-w-md mx-auto px-5 py-20 text-center text-stone-500">Please login to track your bills.</div>;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8" data-testid="bills-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">Personal tracker</div>
          <h1 className="heading text-2xl md:text-4xl font-extrabold text-stone-900">Bills Monitor</h1>
        </div>
        <Button onClick={() => setShowForm(v => !v)} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] min-h-[44px]" data-testid="bills-add-btn">
          <Plus size={14} className="mr-1" /> {showForm ? "Cancel" : "Add bill"}
        </Button>
      </div>

      {/* Summary hero */}
      <div className="rounded-3xl bg-gradient-to-br from-stone-900 to-stone-800 text-white p-5 md:p-6 grid grid-cols-3 gap-3" data-testid="bills-summary">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Unpaid</div>
          <div className="heading text-2xl md:text-3xl font-extrabold mt-1" data-testid="bills-unpaid-total">{inr(summary.total)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Overdue</div>
          <div className="heading text-2xl md:text-3xl font-extrabold mt-1 text-red-300" data-testid="bills-overdue-count">{summary.overdue.length}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Due within 7d</div>
          <div className="heading text-2xl md:text-3xl font-extrabold mt-1 text-amber-300" data-testid="bills-duesoon-count">{summary.dueSoon.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { k: "upcoming", label: "Upcoming",  n: items.filter(i => !i.is_paid && (daysUntil(i.due_date) ?? 0) >= 0).length },
          { k: "overdue",  label: "Overdue",   n: summary.overdue.length },
          { k: "paid",     label: "Paid",      n: items.filter(i => i.is_paid).length },
          { k: "all",      label: "All",       n: items.length },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full min-h-[36px] ${
              tab === t.k ? "bg-[#E05E36] text-white" : "bg-white border border-stone-200 text-stone-700"
            }`}
            data-testid={`bills-tab-${t.k}`}
          >
            {t.label} <span className={`ml-1 text-[10px] font-mono ${tab === t.k ? "text-white/85" : "text-stone-500"}`}>{t.n}</span>
          </button>
        ))}
      </div>

      {showForm && <BillForm onDone={(row) => { setItems(x => [row, ...x]); setShowForm(false); }} />}

      {loading ? <Loader2 className="mx-auto mt-12 animate-spin text-stone-400" /> :
        filtered.length === 0 ? (
          <div className="mt-8 text-center text-stone-500 text-sm" data-testid="bills-empty">
            No bills in this tab. Tap <b>Add bill</b> to log one.
          </div>
        ) : (
          <div className="mt-6 space-y-2" data-testid="bills-list">
            {filtered.map(b => {
              const meta = KIND_META[b.kind] || KIND_META.other;
              const d = daysUntil(b.due_date);
              const late = d !== null && d < 0 && !b.is_paid;
              const soon = d !== null && d >= 0 && d <= 3 && !b.is_paid;
              const Icon = meta.Icon;
              return (
                <div key={b.bill_id} className={`bg-white border rounded-2xl p-4 flex gap-3 items-center ${late ? "border-red-300" : soon ? "border-amber-300" : "border-stone-200"}`} data-testid={`bill-row-${b.bill_id}`}>
                  <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${meta.color}`}><Icon size={20} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                      {b.is_paid && <span className="text-[9px] uppercase tracking-widest font-black text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Paid</span>}
                      {b.recurring_monthly && <span className="text-[9px] uppercase tracking-widest font-black text-stone-700 bg-stone-100 px-1.5 py-0.5 rounded">Monthly</span>}
                      {late && <span className="text-[9px] uppercase tracking-widest font-black text-red-700 bg-red-50 px-1.5 py-0.5 rounded">Overdue</span>}
                      {soon && <span className="text-[9px] uppercase tracking-widest font-black text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Due soon</span>}
                    </div>
                    <div className="font-bold text-stone-900 truncate">{b.biller_name} {b.account_ref ? <span className="text-stone-400 font-mono text-xs">····{b.account_ref}</span> : null}</div>
                    <div className="text-xs text-stone-500">
                      Due {b.due_date} {d !== null && !b.is_paid && (d < 0 ? `· ${-d}d ago` : d === 0 ? "· today" : `· in ${d}d`)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="heading font-black text-stone-900">{inr(b.amount_inr)}</div>
                    <div className="mt-2 flex items-center gap-1 justify-end">
                      {b.pay_url && !b.is_paid && (
                        <a
                          href={b.pay_url}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#E05E36] hover:bg-[#c94e2a] text-white rounded-full px-3 min-h-[36px]"
                          data-testid={`bill-pay-${b.bill_id}`}
                        >Pay <ExternalLink size={11} /></a>
                      )}
                      {!b.is_paid && (
                        <button
                          onClick={() => markPaid(b)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#2D5A27] hover:bg-[#1e3f1a] text-white rounded-full px-3 min-h-[36px]"
                          data-testid={`bill-markpaid-${b.bill_id}`}
                        ><Check size={11} /> Paid</button>
                      )}
                      <button
                        onClick={() => removeItem(b.bill_id)}
                        className="text-[11px] text-stone-500 hover:text-red-600 min-h-[36px] p-1.5"
                        data-testid={`bill-delete-${b.bill_id}`}
                        aria-label="Delete"
                      ><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

function BillForm({ onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ kind: "electric", biller_name: "", amount_inr: "", due_date: today, pay_url: "", account_ref: "", notes: "", recurring_monthly: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.biller_name.trim() || !f.amount_inr || !f.due_date) { toast.error("Biller, amount and due date required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/bills", {
        kind: f.kind,
        biller_name: f.biller_name.trim(),
        amount_inr: Number(f.amount_inr),
        due_date: f.due_date,
        pay_url: f.pay_url || null,
        account_ref: f.account_ref || null,
        notes: f.notes || null,
        recurring_monthly: !!f.recurring_monthly,
      });
      toast.success("Bill added");
      onDone?.(r.data.bill);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="bills-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Type</label>
          <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
            <SelectTrigger className="mt-1" data-testid="bills-form-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map(k => <SelectItem key={k.k} value={k.k}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Biller name</label>
          <Input value={f.biller_name} onChange={(e) => set("biller_name", e.target.value)} placeholder="e.g. Airtel Postpaid" className="mt-1" data-testid="bills-form-biller" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Amount ₹</label>
          <Input type="number" value={f.amount_inr} onChange={(e) => set("amount_inr", e.target.value)} className="mt-1" data-testid="bills-form-amount" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Due date</label>
          <Input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" data-testid="bills-form-due" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Pay URL (optional)</label>
          <Input value={f.pay_url} onChange={(e) => set("pay_url", e.target.value)} placeholder="https://…" className="mt-1" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Acct ref (last 4)</label>
          <Input value={f.account_ref} onChange={(e) => set("account_ref", e.target.value)} maxLength={4} className="mt-1" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-stone-700">
        <input type="checkbox" checked={f.recurring_monthly} onChange={(e) => set("recurring_monthly", e.target.checked)} className="w-4 h-4" data-testid="bills-form-recurring" />
        Recurring monthly — auto-roll next month when I mark this paid
      </label>
      <Button onClick={save} disabled={saving} className="w-full rounded-full bg-[#2D5A27] hover:bg-[#1e3f1a] min-h-[44px]" data-testid="bills-form-save">
        {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> Saving…</> : "Save bill"}
      </Button>
    </div>
  );
}
